import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, money, downloadFile, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { SalesList } from "@/components/SalesList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="num mt-2 text-2xl font-semibold text-[#0B1523]" data-testid={testid}>{value}</div>
  </div>
);

export default function Sales() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");

  useEffect(() => {
    api.get("/quotes/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/quotes", { params: { q, status: status === "all" ? "" : status, type: type === "all" ? "" : type } }).then((r) => setQuotes(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, type]);

  const pdf = async (e, x) => {
    e.stopPropagation();
    try {
      await downloadFile(`/quotes/${x.id}/pdf`, `${x.number}.pdf`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <PageHeader title="Sales" subtitle="Create and manage customer quotations">
        <Button asChild className="bg-[#1BA3E0] hover:bg-[#1590C8]" data-testid="new-quote-btn">
          <Link to="/sales/new"><Plus className="mr-1 h-4 w-4" /> New quote</Link>
        </Button>
      </PageHeader>

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4 fade-up">
          <Stat label="Quotes this month" value={stats.month_quotes} testid="stat-month-quotes" />
          <Stat label="Quoted this month" value={money(stats.month_value)} testid="stat-month-value" />
          <Stat label="Orders this month" value={stats.month_orders} testid="stat-month-orders" />
          <Stat label="Order value this month" value={money(stats.month_order_value)} testid="stat-month-order-value" />
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search quote no, customer, reference" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="quotes-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "draft", "sent", "accepted", "declined"].map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-36" data-testid="quotes-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Quotes & orders</SelectItem>
              <SelectItem value="quote">Quotes only</SelectItem>
              <SelectItem value="order">Orders only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <SalesList quotes={quotes} onOpen={(x) => nav(`/sales/${x.id}`)} onPdf={pdf} />
        </div>
      </div>
    </div>
  );
}
